import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccessList, ListedGroup, Permission } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import ListCard from "../../../ui/layout/ListCard";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";
import { itemKindText } from "../../workflows";
import { overridesChange } from "../changes/accessChanges";
import type { AccessChanges } from "../changes/useAccessChanges";
import { curatedKinds, holds, inclusionChoice, withCuratedKindsHere, withInclusionChoice, type InclusionChoice } from "../model/grantSets";
import type { GroupNamer } from "../model/groupNames";
import { PROJECT_PERMISSIONS_BY_CATEGORY } from "../model/permissionCatalogue";
import CurateKindsDialog from "./CurateKindsDialog";
import PermissionLine, { CategoryHeading } from "./PermissionLine";

interface IncludedPermissionsProps {
  projectId: string;
  group: ListedGroup;
  list: AccessList;
  namer: GroupNamer;
  changes: AccessChanges;
}

/**
 * An installation group in a project (accounts.md GS-1, PD-3 (b)): per permission whether it
 * gives it here, and "as the installation", "on here" or "off here". A choice changes only
 * this group's part here; a person's other groups still add theirs.
 */
export default function IncludedPermissions({ projectId, group, list, namer, changes }: IncludedPermissionsProps) {
  const { t } = useTranslation();
  const [choosingKinds, setChoosingKinds] = useState(false);
  const named = { groupId: group.groupId, name: namer.name(group.groupId), grants: group.grants };
  const kindsHere = curatedKinds(group.overrides.filter((override) => override.override === "on"), list.itemKinds);
  const kindsText = (kinds: readonly string[]) => (kinds.length === list.itemKinds.length ? t("access.kinds.every") : kinds.map((kind) => itemKindText(kind, t)).join(", "));

  const choose = (permission: Permission, choice: InclusionChoice) => {
    const next = withInclusionChoice(group.overrides, permission, choice, group.grants, list.itemKinds);
    changes.run(overridesChange(t, projectId, named, group.overrides, next));
  };

  const line = (permission: Permission) => {
    const fromInstallation = holds(group.grants, permission);
    const choice = inclusionChoice(group.overrides, permission);
    // The mark shows what the group gives here; Curate on here names its kinds on its own button.
    const givenHere = holds(group.effective, permission);
    const mark = !givenHere ? "–" : permission === "curate" && choice !== "on" ? kindsText(curatedKinds(group.effective, list.itemKinds)) : "✓";
    const markMeaning = t(givenHere ? "access.included.given" : "access.included.notGiven");
    return (
      <PermissionLine key={permission} permission={permission} stacked={true}>
        {() => (
          <>
            <span className="shrink-0 text-xs" title={markMeaning}>
              {mark}
              <span className="sr-only">{` ${markMeaning}`}</span>
            </span>
            {permission === "curate" && choice === "on" ? (
              <AppButton variant="ghost" size="sm" disabled={!group.manageable} onClick={() => { setChoosingKinds(true); }}>{kindsText(kindsHere)}</AppButton>
            ) : null}
            <SegmentedControl<InclusionChoice>
              label={t(`access.permissions.${permission}`)}
              selected={choice}
              options={[
                { key: "inherit", label: t("access.included.inherit"), disabled: !group.manageable || changes.busy },
                { key: "on", label: t("access.included.on"), disabled: !group.manageable || changes.busy || (choice !== "on" && !list.givable.has(permission)) },
                { key: "off", label: t("access.included.off"), disabled: !group.manageable || changes.busy || (choice !== "off" && !fromInstallation) },
              ]}
              onSelect={(next) => { if (next !== choice) choose(permission, next); }}
            />
          </>
        )}
      </PermissionLine>
    );
  };

  return (
    <ListCard title={t("access.permissionsTitle")}>
      <ul className="app-divider-top pb-2">
        {PROJECT_PERMISSIONS_BY_CATEGORY.map(({ category, permissions }) => (
          <li key={category}>
            <ul>
              <CategoryHeading>{t(`access.categories.${category}`)}</CategoryHeading>
              {permissions.map(line)}
            </ul>
          </li>
        ))}
      </ul>
      {choosingKinds ? (
        <CurateKindsDialog title={t("access.kinds.titleHere", { group: named.name })} itemKinds={list.itemKinds} chosen={kindsHere}
          onSave={(kinds) => { changes.run(overridesChange(t, projectId, named, group.overrides, withCuratedKindsHere(group.overrides, kinds))); }}
          onClose={() => { setChoosingKinds(false); }} />
      ) : null}
    </ListCard>
  );
}
